/** Trailing digits an operator reads off a terminal. */
const EPI_LAST_DIGITS = 4;

/**
 * A Valor EPI as it should appear on any money screen.
 *
 * Shared rather than repeated, because it is a BlueSage-domain rule and not a
 * property of any one component: the digits a device shows are its last four,
 * and the full EPI is noise an operator has to read past. The bullets are part
 * of the format — `7943` alone reads as an amount or an order number.
 */
export const maskEpi = (epi: string | null | undefined): string => maskLast4(epi);

/**
 * The same masking for a card's last four.
 *
 * A card number and a terminal EPI are different things that read identically
 * once masked, so they share the format rather than each inventing one — but
 * they get their own names, because `maskEpi(cardLast4)` would be a lie about
 * what is being masked.
 */
export const maskLast4 = (value: string | null | undefined): string =>
    value ? `••••${ value.slice(-EPI_LAST_DIGITS) }` : '';

/**
 * Minor units per major unit.
 *
 * Amounts are integer cents end to end — in the store, on the wire, and in the
 * mutation — and dollars exist only where a human reads or types one. Keeping
 * the conversion in one place is what stops that boundary being crossed twice
 * by accident, which is the failure that multiplies a charge by a hundred.
 */
export const MINOR_UNITS_PER_MAJOR = 100;

/** What a currency input gives us → what every action and mutation takes. */
export const toMinorUnits = (amount: number): number =>
    Math.round((amount ?? 0) * MINOR_UNITS_PER_MAJOR);

/** What the store holds → what a currency pipe renders. */
export const toMajorUnits = (minorUnits: number): number =>
    (minorUnits ?? 0) / MINOR_UNITS_PER_MAJOR;

/**
 * Minor units as an operator reads them, for the one place a row has to state a
 * figure the currency pipe is not already formatting.
 *
 * `Intl` rather than a hard-coded `$`: the amounts on these rows are USD today,
 * and a symbol baked into a shared helper is the kind of thing that is still
 * there when it is not.
 */
export function formatMinorUnits(minor: number, currency: string | null | undefined): string {
    const major = minor / MINOR_UNITS_PER_MAJOR;

    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency || 'USD',
        }).format(major);
    } catch {
        // An unknown currency code must not take a money row down with it.
        return major.toFixed(2);
    }
}

/**
 * What the CARD was charged, when that is not what the job was credited.
 *
 * `Transaction.amount` is always the BASE — the figure that reconciles against
 * the job total — so a $2.00 terminal sale on a 3% location where the customer
 * tipped $0.40 shows **$2.00** while **$2.46** actually left their card. The row
 * was the only place an operator could have seen that, and it said nothing: the
 * question a customer actually asks — *how much did you take?* — had no answer
 * on screen.
 *
 * Reads the recorded figures and never recomputes them. `amount = base_amount +
 * tip + differential + tax` is the contract invariant, and the settle handler
 * omits each key when it is zero, so an absent part is a zero part.
 *
 * Renders nothing at all when the card paid exactly the base, which is every
 * sale on a location without dual pricing and without a tip. Each component is
 * named only when non-zero, so the common dual-pricing case reads
 * `· $2.06 charged — $0.06 differential` with no mention of a tip nobody left.
 *
 * Grouped by `Intl.NumberFormat`, so it stays legible at YMM's real ticket
 * sizes: `· $12,360.00 charged — $360.00 tip, $360.00 differential`. It is a
 * block-level line on the row and wraps rather than truncating, because a
 * money figure cut off by an ellipsis is worse than one on two lines.
 */
function chargedBreakdown(
    metadata: Record<string, unknown>,
    currency: string | null | undefined,
    creditedMinor: number,
): string {
    const minor = (value: unknown): number => {
        const n = Number(value);

        return Number.isFinite(n) && n > 0 ? n : 0;
    };

    const paid = minor(metadata.bluesageAmountPaid);
    if (!paid || paid === creditedMinor) { return ''; }

    const parts = [
        [ minor(metadata.bluesageTip), 'tip' ] as const,
        [ minor(metadata.bluesageDifferential), 'differential' ] as const,
        [ minor(metadata.bluesageTax), 'tax' ] as const,
    ]
        .filter(([ value ]) => value > 0)
        .map(([ value, label ]) => `${ formatMinorUnits(value, currency) } ${ label }`);

    const detail = parts.length ? ` — ${ parts.join(', ') }` : '';

    return ` · ${ formatMinorUnits(paid, currency) } charged${ detail }`;
}

/**
 * What a transaction row is, in BlueSage terms, or `undefined` when it is not
 * ours at all.
 *
 * `Transaction.type` says how the money was FILED — payment, deposit, refund —
 * which is an accounting answer and not the one an operator looking at a job
 * needs. Two rows reading "Deposit (BlueSagePay) $1.50" can be a completed sale
 * and an open authorization holding a customer's money hostage, and nothing on
 * the row told them apart. That is what YMM hit on 2026-08-28: *"the
 * transactions in the transactions table don't show if its sale or preauth"*.
 *
 * Everything here is read from metadata the CRM itself wrote or an event
 * delivered — nothing is inferred from the amount or the stage.
 */
/** Where an online hold was taken, for the row. Absent on a terminal hold. */
function onlineHoldRail(metadata: Record<string, unknown>): string | undefined {
    if (metadata.bluesageHold !== true) { return undefined; }
    switch (metadata.bluesageChannel) {
        case 'pay_link': return 'hold link';
        case 'card_on_file': return 'card on file';
        default: return 'card entered in the CRM';
    }
}

export function describeBlueSageRail(transaction: {
    stage?: string | null;
    currency?: string | null;
    /** The BASE credited to the job, in minor units — what the row displays. */
    amount?: number | null;
    metadata?: Record<string, unknown> | null;
} | null | undefined): string | undefined {
    const metadata = transaction?.metadata;
    if (!metadata) { return undefined; }

    // ⚠ The device is named on every terminal row, and it is load-bearing on a
    // hold: a completion can ONLY be sent to the terminal the authorization was
    // taken on — the middleware overwrites the EPI with the auth's own — so an
    // operator standing in front of two readers has to be told which one.
    const epi = typeof metadata.bluesageTerminalEpi === 'string'
        ? maskEpi(metadata.bluesageTerminalEpi)
        : '';
    const at = epi ? ` · ${ epi }` : '';

    // ⚠ A RELEASED hold is not a held one, and it used to say it was. Releasing
    // closes the transaction `cancelled`, but this branch keyed only on "has a
    // preauth ref and is not paid" — so a hold whose money had already been let
    // go still read *"money HELD, not taken"*. That is the opposite of what
    // happened, on the row an operator checks to find out.
    //
    // `cancelled` alone cannot say it: a push that was REFUSED also ends
    // cancelled, and nothing ever reached the card there. The marker is what
    // separates them.
    if (metadata.bluesagePreauthRef && metadata.bluesagePreauthReleased) {
        return `Authorization released — the hold was returned to the customer's card${ at }`;
    }

    // An open hold next, because it is the one with a customer's money frozen
    // behind it and the one that needs an action taken. It stops being a hold
    // the moment the transaction is paid: at that point the ticket completed and
    // the row is a payment like any other.
    // A completed online hold, before BlueSage's event settles it. The hold is
    // gone; saying "HELD" would invite a second completion.
    if (metadata.bluesagePreauthRef && metadata.bluesageCapturedAt && transaction?.stage === 'pending') {
        return 'Hold completed — waiting for BlueSage to confirm the final amount';
    }

    if (metadata.bluesagePreauthRef && transaction?.stage !== 'paid') {
        // What the CARD is holding — base plus differential on a dual-pricing
        // location — because that is the figure on the customer's screen and
        // the one they quote back. Absent means the base.
        const held = Number(metadata.bluesageAmountPaid);
        const heldNote = Number.isFinite(held) && held > 0
            ? ` · ${ formatMinorUnits(held, transaction?.currency) } held on the card`
            : '';
        const how = onlineHoldRail(metadata);
        return `Authorization — money HELD, not taken${ heldNote }${ how ? ` · ${ how }` : '' }${ at }`;
    }

    // Requirement: a row closed because the card was DECLINED says so. `Cancelled`
    // alone reads as something an operator did, and the row is the only place
    // left to find out why a payment that was attempted is not on the job.
    //
    // Keyed on the marker the server sets when it closes a row on a decline, and
    // on nothing else. The decline REASON cannot carry this: an event rewrites
    // it with its own wording, and it stays behind on rows that later close for
    // another reason — a link that expires after a declined attempt, for one.
    if (transaction?.stage === 'cancelled' && metadata.bluesageCardDeclined === true) {
        return 'Card declined — nothing was charged';
    }

    // A refund leg says WHY, because that is the whole point of asking for a
    // reason — it used to be sent to BlueSage and recorded nowhere the CRM could
    // show it, so the refund ledger read as a column of blanks.
    //
    // ⚠ NOT on a cancelled leg. `describeBlueSageRefundFailure` already says
    // "nothing was refunded" there, and printing the reason underneath it read
    // as a contradiction — a release that failed showed *"Nothing was refunded
    // — this hold can no longer be released"* with *"Authorization release"*
    // directly beneath, which is the two halves disagreeing on one row.
    if (transaction?.stage !== 'cancelled'
        && typeof metadata.bluesageRefundReason === 'string'
        && metadata.bluesageRefundReason) {
        // ⚠ Name the figure that actually left the merchant account when it is
        // not the one on the row. A $1.23 completion on a dual-pricing location
        // captures $1.27, and voiding it returns all $1.27 — but the leg reads
        // −$1.23, because `Transaction.amount` is always the base credited to
        // the job. Saying only the base leaves the operator unable to answer the
        // question the customer will actually ask.
        const paid = Number(metadata.bluesageAmountPaid);
        const returned = Number.isFinite(paid) && paid > 0
            ? ` · ${ formatMinorUnits(paid, transaction?.currency) } returned to the card`
            : '';

        return `${ metadata.bluesageRefundReason }${ returned }`;
    }

    // What the CARD was charged, when it differs from what the job was credited.
    //
    // `Transaction.amount` is always the BASE — the figure that has to reconcile
    // against the job total — so a $2.00 sale on a dual-pricing location with a
    // tip shows $2.00 while $2.46 actually left the customer's card. The row was
    // the one place an operator could have seen that and it said nothing, so the
    // question "how much did they actually pay?" had no answer on screen.
    //
    // Each part appears only when it is non-zero: a location without dual
    // pricing never renders a differential, and a sale without a tip never
    // renders a tip. On a sale where the card paid exactly the base — the common
    // case off a location with no dual pricing — the whole clause is absent.
    const charged = chargedBreakdown(
        metadata, transaction?.currency, Math.abs(transaction?.amount ?? 0),
    );

    switch (metadata.bluesageChannel) {
        case 'terminal': return `Terminal${ at }${ charged }`;
        // Keyed on the DEVICE rather than pushed from here, which is the
        // distinction that decides whether a partial refund is possible at all.
        case 'terminal_standalone': return `Terminal (keyed on the device)${ at }${ charged }`;
        case 'embedded': return `Card entered in the CRM${ charged }`;
        case 'card_on_file': return `Card on file${ charged }`;
        case 'pay_link': return `Pay link${ charged }`;
        case 'hosted_page': return `Hosted page${ charged }`;
        default: break;
    }

    // No channel yet, but a link was minted — the customer has not paid it, so
    // no event has told us how they eventually will.
    if (metadata.bluesagePayUrl && metadata.bluesageHold === true && transaction?.stage === 'pending') {
        return 'Hold link — the customer has not authorized it yet';
    }
    if (metadata.bluesagePayUrl) { return 'Pay link — not paid yet'; }

    return undefined;
}

/**
 * Why a BlueSage row still says `Pending`, when we know the card was approved.
 *
 * Settlement is asynchronous: the row becomes `paid` only when
 * `transaction.approved` arrives and `markPaid` applies it, and that event's
 * own `occurred_at` was measured 34 to 218 seconds behind the charge on
 * production. For that whole window a bare "Pending" is technically true and
 * practically a lie — the customer's card has already been debited, and an
 * operator reading it assumes the payment failed and takes another one.
 *
 * Returns `undefined` for every row where "Pending" already means what it says:
 * a hold that is meant to stay pending, an unpaid pay link, a push nobody has
 * answered yet.
 */
export function describeBlueSagePendingReason(transaction: {
    stage?: string | null;
    metadata?: Record<string, unknown> | null;
} | null | undefined): string | undefined {
    if (transaction?.stage !== 'pending') { return undefined; }

    const metadata = transaction?.metadata;
    if (!metadata) { return undefined; }

    // A hold is SUPPOSED to sit pending. Saying it is "settling" would suggest
    // it is on its way to becoming a payment on its own, which it is not — a
    // hold becomes a payment only when somebody completes it.
    if (metadata.bluesagePreauthRef && metadata.bluesageCaptureUnconfirmed && !metadata.bluesageCapturedAt) {
        return 'Completion not confirmed yet — do not take another payment until this updates';
    }

    if (metadata.bluesagePreauthRef) { return undefined; }

    if (metadata.bluesageApprovedAt) {
        return 'Approved — waiting for BlueSage to confirm the final amount';
    }

    if (metadata.bluesagePayUrl && metadata.bluesageHold === true) {
        return 'Waiting for the customer to authorize the hold link';
    }
    if (metadata.bluesagePayUrl) { return 'Waiting for the customer to pay the link'; }

    return undefined;
}


/**
 * How long a refund leg may read `pending` before it is treated as abandoned.
 *
 * A BlueSage refund answers the browser as soon as its leg exists and settles in
 * the background, so a `pending` leg normally means "the terminal is still
 * working". The server gives up on one after 450 s (`REFUND_LEG_STALE_AFTER_SECONDS`
 * in `bluesage.refund.ts`) and asks BlueSage what happened the next time
 * somebody tries; this is that, plus a minute for a browser clock that disagrees
 * with the server's.
 */
export const BLUESAGE_REFUND_LEG_STALE_AFTER_SECONDS = 510;

export const BLUESAGE_REFUND_IN_PROGRESS_REASON =
    'A refund or release of this payment is still being confirmed.';

export const BLUESAGE_OPEN_PAY_LINK_DELETE_REASON =
    'The customer can still pay this link. Cancel the pay link instead.';

export const BLUESAGE_OPEN_HOLD_DELETE_REASON =
    'This is an open authorization on the customer\'s card. Cancel the pre-auth instead.';

export const BLUESAGE_COMPLETING_HOLD_DELETE_REASON =
    'This hold has been completed and is waiting for BlueSage to confirm the final amount. '
    + 'Wait for it to settle, then refund it if needed.';

// Requirement: a hold link the customer has completed is a HOLD, not an open
// link. BlueSage refuses to cancel it, and the money is released instead.
export function hasOpenBlueSagePayLink(transaction: {
    stage?: string | null;
    deletedAt?: number | null;
    metadata?: Record<string, unknown> | null;
} | null | undefined): boolean {
    return Boolean(transaction)
        && !transaction.deletedAt
        && transaction.stage === 'pending'
        && Boolean(transaction.metadata?.bluesageLinkId)
        && !transaction.metadata?.bluesagePreauthRef;
}

/**
 * An ONLINE hold: a keyed card or hold link authorized instead of charged.
 *
 * Completed with a capture, not on a terminal — there is no device to send it
 * to. A terminal authorization never carries `bluesageHold`.
 */
export function isOnlineBlueSageHold(transaction: {
    metadata?: Record<string, unknown> | null;
} | null | undefined): boolean {
    return transaction?.metadata?.bluesageHold === true;
}

interface BlueSageRefundLegLike {
    id: string;
    stage?: string | null;
    deletedAt?: number | null;
    createdAt?: number | null;
    metadata?: Record<string, unknown> | null;
}

/** The live refund legs posted against one transaction, as the job holds them. */
export function blueSageRefundLegsOf<T extends BlueSageRefundLegLike>(
    transactions: T[] | null | undefined,
    transactionId: string | undefined,
): T[] {
    if (!transactionId) { return []; }

    return (transactions ?? [])
        .filter((t) => !t.deletedAt && t.metadata?.refunds === transactionId);
}

/**
 * The leg THIS attempt created: the newest one that was not there when the
 * operator pressed Confirm. Undefined until the job has been read again.
 */
export function blueSageRefundLegOfAttempt<T extends BlueSageRefundLegLike>(
    transactions: T[] | null | undefined,
    transactionId: string | undefined,
    knownLegIds: string[],
): T | undefined {
    return blueSageRefundLegsOf(transactions, transactionId)
        .filter((leg) => !knownLegIds.includes(leg.id))
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))[0];
}

/**
 * Is a refund or release of this transaction still being confirmed?
 *
 * Requirement: the server refuses a second refund while one is running, and the
 * screen must not offer what the server will refuse. A leg older than any refund
 * can run is NOT in progress — the request settling it is gone, and pressing
 * again is what makes the server go and find out what happened to it.
 */
export function blueSageRefundInProgress(
    transactions: BlueSageRefundLegLike[] | null | undefined,
    transactionId: string | undefined,
    nowSeconds: number = Math.floor(Date.now() / 1000),
): boolean {
    return blueSageRefundLegsOf(transactions, transactionId).some((leg) =>
        leg.stage === 'pending'
        && typeof leg.createdAt === 'number'
        && nowSeconds - leg.createdAt < BLUESAGE_REFUND_LEG_STALE_AFTER_SECONDS);
}

/**
 * Why a refund leg is sitting at `cancelled`, in the operator's language.
 *
 * A cancelled refund leg means **no money moved** — `refundTransaction` cancels
 * its own leg when the rail refuses, precisely so job totals never count a
 * refund that did not happen. But the row it leaves behind reads
 * *"Refund (BlueSagePay) −$1.50 · Cancelled"* and says nothing about why, which
 * on 2026-08-28 was mistaken for a refund that had gone wrong silently.
 *
 * The common case is a **release of an authorization** the device no longer
 * holds: releasing routes through the refund mutation, so a hold that was voided
 * at the terminal leaves exactly this row.
 */
export function describeBlueSageRefundFailure(transaction: {
    stage?: string | null;
    type?: string | null;
    metadata?: Record<string, unknown> | null;
} | null | undefined): string | undefined {
    if (transaction?.stage !== 'cancelled') { return undefined; }

    const failure = transaction?.metadata?.bluesageRefundFailed;
    if (typeof failure !== 'string' || !failure) { return undefined; }

    // Requirement: "nothing was refunded" is only true of a REFUSAL. When Valor
    // timed out the terminal may already have acted — on production 2026-09-17 a
    // hold release answered that way had in fact voided on the device — so the
    // server marks those legs and the row says the outcome is not confirmed.
    if (transaction?.metadata?.bluesageRefundUnconfirmed === true) {
        return `Not confirmed — ${ failure }`;
    }

    return `Nothing was refunded — ${ failure }`;
}
